export const MESSENGER_V2_SFU_CAMERA_CAPTURE_OPTIONS = Object.freeze({
  frameRate: Object.freeze({ ideal: 24, max: 30 }),
  facingMode: Object.freeze({ ideal: 'user' }),
  resizeMode: Object.freeze({ ideal: 'none' }),
});

export const MESSENGER_V2_SFU_CAMERA_PUBLISH_OPTIONS = Object.freeze({
  simulcast: false,
});

export const MESSENGER_V2_SFU_AUDIO_CAPTURE_OPTIONS = Object.freeze({
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
  channelCount: 1,
});

export const resolveMessengerV2NativeCameraConstraints = () => ({
  audio: false,
  video: {
    frameRate: { ...MESSENGER_V2_SFU_CAMERA_CAPTURE_OPTIONS.frameRate },
    facingMode: { ...MESSENGER_V2_SFU_CAMERA_CAPTURE_OPTIONS.facingMode },
    resizeMode: { ...MESSENGER_V2_SFU_CAMERA_CAPTURE_OPTIONS.resizeMode },
  },
});

export const resolveMessengerV2InitialMediaConstraints = (mode = 'voice') => ({
  audio: { ...MESSENGER_V2_SFU_AUDIO_CAPTURE_OPTIONS },
  video: mode === 'video' ? resolveMessengerV2NativeCameraConstraints().video : false,
});

const resolveBrowserMediaDevices = () =>
  typeof navigator !== 'undefined' ? navigator.mediaDevices : null;

const resolveBrowserWakeLock = () =>
  typeof navigator !== 'undefined' ? navigator.wakeLock : null;

const resolveBrowserDocument = () => (typeof document !== 'undefined' ? document : null);

export const createMessengerV2InitialCallMedia = async ({
  mode = 'voice',
  mediaDevices = resolveBrowserMediaDevices(),
} = {}) => {
  if (!mediaDevices?.getUserMedia) {
    throw new Error('Call media capture is not supported by this browser');
  }

  const stream = await mediaDevices.getUserMedia(
    resolveMessengerV2InitialMediaConstraints(mode),
  );
  const audioTrack = stream?.getAudioTracks?.()[0] ?? null;
  const videoTrack = stream?.getVideoTracks?.()[0] ?? null;
  if (!audioTrack || (mode === 'video' && !videoTrack)) {
    stream?.getTracks?.().forEach((track) => track?.stop?.());
    throw new Error(
      !audioTrack
        ? 'Microphone capture did not return an audio track'
        : 'Camera capture did not return a video track',
    );
  }

  return {
    stream,
    tracks: mode === 'video' ? [audioTrack, videoTrack] : [audioTrack],
  };
};

export const requestMessengerV2CallScreenWakeLock = async ({
  wakeLock = resolveBrowserWakeLock(),
  documentRef = resolveBrowserDocument(),
} = {}) => {
  if (documentRef?.visibilityState === 'hidden' || typeof wakeLock?.request !== 'function') {
    return null;
  }

  try {
    return await wakeLock.request('screen');
  } catch {
    return null;
  }
};

export const createMessengerV2NativeCameraTrack = async ({
  mediaDevices = resolveBrowserMediaDevices(),
} = {}) => {
  if (!mediaDevices?.getUserMedia) {
    throw new Error('Camera capture is not supported by this browser');
  }

  const stream = await mediaDevices.getUserMedia(resolveMessengerV2NativeCameraConstraints());
  const track = stream?.getVideoTracks?.()[0] ?? null;
  if (!track) {
    stream?.getTracks?.().forEach((mediaTrack) => mediaTrack?.stop?.());
    throw new Error('Camera capture did not return a video track');
  }

  return { stream, track };
};

export const resolveMessengerV2SfuRoomMediaOptions = () => ({
  // The call UI renders the complete camera frame at every viewport size.
  // LiveKit's adaptive subscription can switch portrait mobile publishers
  // to a differently-shaped simulcast rendition when the receiving video
  // element becomes wide, leaving the incoming pixels cropped before CSS
  // object-fit is applied. Keep one stable camera rendition instead.
  adaptiveStream: false,
  dynacast: false,
  disconnectOnPageLeave: false,
  audioCaptureDefaults: MESSENGER_V2_SFU_AUDIO_CAPTURE_OPTIONS,
  publishDefaults: MESSENGER_V2_SFU_CAMERA_PUBLISH_OPTIONS,
});
